import { Autocomplete, SxProps, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { AvailablePeriod, PeriodBaseRequest, PeriodType } from '../store/types';

interface Props {
  sx?: SxProps;
  values: PeriodBaseRequest[];
  onChange: (periods: PeriodBaseRequest[]) => void;
  disableInceptionRemoval: boolean;
  disabled?: boolean;
  maxPeriods: number;
  label?: string;
}

export const AvailablePeriods: AvailablePeriod[] = [
  { displayName: '1 Month (1M)', periodType: PeriodType.Month, periodValue: 1 },
  { displayName: '3 Months (3M)', periodType: PeriodType.Month, periodValue: 3 },
  { displayName: '6 Months (6M)', periodType: PeriodType.Month, periodValue: 6 },
  { displayName: '1 Year (1Y)', periodType: PeriodType.Year, periodValue: 1 },
  { displayName: 'Financial Year (FY)', periodType: PeriodType.FinancialYear, periodValue: 0 },
  { displayName: 'Calendar Year (CY)', periodType: PeriodType.CalendarYear, periodValue: 0 },
  { displayName: '2 Years (2Y)', periodType: PeriodType.Year, periodValue: 2 },
  { displayName: '3 Years (3Y)', periodType: PeriodType.Year, periodValue: 3 },
  { displayName: '4 Years (4Y)', periodType: PeriodType.Year, periodValue: 4 },
  { displayName: '5 Years (5Y)', periodType: PeriodType.Year, periodValue: 5 },
  { displayName: '7 Years (7Y)', periodType: PeriodType.Year, periodValue: 7 },
  { displayName: 'Since Inception (SI)', periodType: PeriodType.SinceInception, periodValue: 0 },
];

export const DefaultAvailablePeriods: AvailablePeriod[] = [
  { displayName: '1 Month (1M)', periodType: PeriodType.Month, periodValue: 1 },
  { displayName: '3 Months (3M)', periodType: PeriodType.Month, periodValue: 3 },
  { displayName: '6 Months (6M)', periodType: PeriodType.Month, periodValue: 6 },
  { displayName: '1 Year (1Y)', periodType: PeriodType.Year, periodValue: 1 },
  { displayName: '3 Years (3Y)', periodType: PeriodType.Year, periodValue: 3 },
  { displayName: '5 Years (5Y)', periodType: PeriodType.Year, periodValue: 5 },
  { displayName: 'Since Inception (SI)', periodType: PeriodType.SinceInception, periodValue: 0 },
];

export const MultiplePeriodSelector = ({ sx, label, values, disabled, onChange, maxPeriods, disableInceptionRemoval }: Props): JSX.Element => {
  const [selectedPeriods, setSelectedPeriods] = useState<AvailablePeriod[]>([]);

  useEffect(() => {
    const mapped = values
      .map((v) =>
        AvailablePeriods.find((p) => p.periodType === PeriodType[v.periodType as keyof typeof PeriodType] && p.periodValue === v.periodValue)
      )
      .filter((p): p is AvailablePeriod => !!p);
    setSelectedPeriods(mapped.sort((a: AvailablePeriod, b: AvailablePeriod) => getPeriodSize(a) - getPeriodSize(b)));
  }, [values]);

  const handleChange = (_: React.SyntheticEvent, value: AvailablePeriod[]) => {
    if (value.length > maxPeriods) return;

    const filtered = (
      disableInceptionRemoval ? [...value, ...selectedPeriods.filter((p) => p.periodType === PeriodType.SinceInception && !value.includes(p))] : value
    ).sort((a: AvailablePeriod, b: AvailablePeriod) => getPeriodSize(a) - getPeriodSize(b));

    setSelectedPeriods(filtered);

    const requests: PeriodBaseRequest[] = filtered.map((period) => ({
      periodType: period.periodType.name,
      periodValue: period.periodValue,
    }));
    onChange(requests);
  };

  const getPeriodSize = (period: AvailablePeriod) => {
    switch (period.periodType) {
      case PeriodType.Month:
        return period.periodValue ?? 0;
      case PeriodType.Year:
        return (period.periodValue ?? 0) * 12;
      case PeriodType.FinancialYear:
      case PeriodType.CalendarYear:
        return 12;
      case PeriodType.SinceInception:
        return Number.MAX_SAFE_INTEGER;
      default:
        return 0;
    }
  };

  return (
    <Autocomplete
      disabled={disabled}
      noOptionsText="No items to select"
      fullWidth
      multiple
      value={selectedPeriods}
      options={AvailablePeriods}
      getOptionLabel={(option) => option.displayName}
      isOptionEqualToValue={(opt, val) => opt.displayName === val.displayName}
      onChange={handleChange}
      getOptionDisabled={(option) =>
        (selectedPeriods.length >= maxPeriods && !selectedPeriods.includes(option)) ||
        (disableInceptionRemoval && option.periodType === PeriodType.SinceInception && selectedPeriods.includes(option))
      }
      renderInput={(params) => <TextField {...params} label={label || 'Select Available Periods'} margin="normal" variant="outlined" fullWidth />}
      sx={sx}
    />
  );
};
